// Plays assistant replies as speech using Amazon Polly via the server's /speak
// endpoint. One audio element at a time; starting a new utterance cancels the
// previous one.

const HTTP_URL = import.meta.env.VITE_ASSISTANT_HTTP_URL ?? 'http://localhost:8080'

let current: HTMLAudioElement | null = null

export interface SpeakHandlers {
  onStart?: () => void
  onEnd?: () => void
}

// ─── Live audio level (0..1) for the reactive voice UI ───
type LevelListener = (level: number) => void
const levelListeners = new Set<LevelListener>()
let audioCtx: AudioContext | null = null
let analyser: AnalyserNode | null = null
let freqData: Uint8Array | null = null
let rafId = 0

/** Subscribe to the assistant's live speaking amplitude (0..1). Returns an unsubscribe fn. */
export function subscribeVoiceLevel(cb: LevelListener): () => void {
  levelListeners.add(cb)
  return () => levelListeners.delete(cb)
}

function emitLevel(level: number) {
  levelListeners.forEach((cb) => cb(level))
}

function startLevelMonitor(audio: HTMLAudioElement) {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    if (!audioCtx) audioCtx = new Ctx()
    if (audioCtx.state === 'suspended') void audioCtx.resume()

    const src = audioCtx.createMediaElementSource(audio)
    analyser = audioCtx.createAnalyser()
    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.75
    src.connect(analyser)
    analyser.connect(audioCtx.destination)
    freqData = new Uint8Array(analyser.frequencyBinCount)

    const loop = () => {
      if (!analyser || !freqData) return
      analyser.getByteFrequencyData(freqData)
      let sum = 0
      for (let i = 0; i < freqData.length; i++) sum += freqData[i] * freqData[i]
      const rms = Math.sqrt(sum / freqData.length) / 255 // 0..1
      emitLevel(Math.min(1, rms * 2.4)) // boost so speech reads clearly
      rafId = requestAnimationFrame(loop)
    }
    loop()
  } catch {
    /* Web Audio unsupported or element already tapped — skip reactivity. */
  }
}

function stopLevelMonitor() {
  if (rafId) cancelAnimationFrame(rafId)
  rafId = 0
  analyser = null
  freqData = null
  emitLevel(0)
}

/** Fetch TTS audio for `text` (optionally in a specific voice/language) and play it. */
export async function speak(
  text: string,
  voice?: string,
  lang?: string,
  handlers: SpeakHandlers = {},
): Promise<void> {
  stopSpeaking()

  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 20000)
  let res: Response
  try {
    res = await fetch(`${HTTP_URL}/speak`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, voice, lang }),
      signal: controller.signal,
    })
  } finally {
    window.clearTimeout(timer)
  }
  if (!res.ok) throw new Error(`speak failed: ${res.status}`)

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const audio = new Audio(url)
  // Aura 2 delivers a fairly slow cadence; nudge playback slightly faster for a
  // more natural, responsive feel.
  audio.playbackRate = 1.12
  current = audio

  startLevelMonitor(audio)
  handlers.onStart?.()

  await new Promise<void>((resolve) => {
    let done = false
    const cleanup = () => {
      if (done) return
      done = true
      window.clearTimeout(safety)
      stopLevelMonitor()
      URL.revokeObjectURL(url)
      if (current === audio) current = null
      handlers.onEnd?.()
      resolve()
    }
    // Safety net: never let a stuck 'ended' event hang the whole turn. Resolve
    // after the clip's duration + slack, or a hard cap if duration is unknown.
    const safety = window.setTimeout(cleanup, 30000)
    audio.onended = cleanup
    audio.onerror = cleanup
    audio.onloadedmetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        window.clearTimeout(safety)
        window.setTimeout(cleanup, (audio.duration / (audio.playbackRate || 1)) * 1000 + 1500)
      }
    }
    void audio.play().catch(cleanup)
  })
}

/** Stop any in-progress speech immediately (used for barge-in / mode changes). */
export function stopSpeaking(): void {
  if (current) {
    current.pause()
    current.src = ''
    current = null
  }
  stopLevelMonitor()
}
